import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, LoaderCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { acceptAdminInvitation } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AcceptInvitation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token")?.trim() || "";
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!token) {
      setError("This invitation link is incomplete.");
      return;
    }
    if (password.length < 12) {
      setError("Use at least 12 characters for your password.");
      return;
    }
    if (password !== confirmPassword) {
      setError("The passwords do not match.");
      return;
    }

    setBusy(true);
    try {
      await acceptAdminInvitation(token, password);
      toast.success("Invitation accepted. Sign in with your new password.");
      navigate("/", { replace: true });
    } catch (requestError) {
      setError(requestError?.message || "Could not accept this invitation.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "#F0F6F1" }}>
      <div className="w-full max-w-md rounded-2xl border border-[#0E5B52]/10 bg-white p-8 shadow-sm">
        <img src="/shopbook-logo.png" alt="Shopbook" className="h-12 w-12 object-contain" />
        <div className="mt-6 inline-flex items-center gap-2 pill" style={{ background: "#D1FAE5", color: "#065F46" }}>
          <ShieldCheck className="h-3 w-3" /> Admin invitation
        </div>
        <h1 className="mt-4 font-display text-3xl font-semibold text-[#064E3B]">Create your password</h1>
        <p className="mt-2 text-sm text-[#4B5563]">
          Complete your Shopbook admin invitation with a secure password.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#4B5563]">
              Password
            </label>
            <Input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="h-12 rounded-xl"
              disabled={busy}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#4B5563]">
              Confirm password
            </label>
            <Input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              className="h-12 rounded-xl"
              disabled={busy}
            />
          </div>
          {error && <p className="text-sm text-[#B91C1C]">{error}</p>}
          <Button type="submit" disabled={busy || !token} className="h-12 w-full bg-[#0E5B52] text-white hover:bg-[#0B4A43]">
            {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <>Accept invitation <ArrowRight className="ml-1.5 h-4 w-4" /></>}
          </Button>
        </form>
        <Link to="/" className="mt-6 block text-center text-sm font-medium text-[#0E5B52]">
          Return to sign in
        </Link>
      </div>
    </div>
  );
}
